/**
 * Tiny client-side text-search helper used across list pages.
 *
 * Why client-side: we already paginate/cap at a couple hundred rows
 * server-side; threading a `q` parameter through every backend
 * endpoint would be a much larger lot. Once any list crosses
 * thousands of rows we'll want a backend search — but at that
 * point the matching rules differ per entity anyway, so a generic
 * client helper is a poor fit and a per-feature backend index is
 * the right answer.
 *
 * The matcher is case-insensitive, accent-insensitive, and matches
 * on substring (no fancy ranking) so the UX matches what users
 * expect from a `Cmd+F`-style filter.
 */

/** Fold + lowercase a string so the match ignores diacritics. */
function fold(s: string): string {
  // String.normalize('NFD') splits combining marks; the regex strips
  // them (̀-ͯ is the Unicode combining-diacritics block).
  // Locale-aware lowercasing keeps Turkish / German edge cases sane
  // (cf. `İ` → `i`).
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase();
}

/**
 * Returns true when every whitespace-separated token in `query`
 * appears (in any order) in any of the strings produced by
 * `fields` for `item`. Empty query → always true.
 */
export function matchesQuery<T>(
  item: T,
  query: string,
  fields: Array<(item: T) => string | number | null | undefined>,
): boolean {
  const trimmed = query.trim();
  if (trimmed.length === 0) return true;
  const haystack = fields
    .map((f) => f(item))
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map((v) => fold(String(v)))
    .join('  '); // Unicode separator so tokens don't collide.
  const tokens = trimmed.split(/\s+/).map(fold);
  return tokens.every((tok) => haystack.includes(tok));
}

/** Re-export the comparator for sites that need it directly. */
export { fold as foldString };
